/**
 * FAC-09 (issue #166) — re-rating a place, without editing it.
 *
 * "Condition ratings can be updated independently of other fields, to reflect
 * ongoing maintenance status without a full edit." The API has been able to
 * do this since #164/#165: `condition` is a field of the correction
 * endpoints, `condition_rated_at` is stamped from the server clock beside it,
 * and a patch naming only the rating touches nothing else. What did not exist
 * was the GESTURE — the only way to say "the nets are torn" was to open the
 * correction form, which asks for the name, the city, the country, the place
 * type and the pin, none of which the person noticing the nets came to change.
 *
 * THIS MODULE IS THE RULES OF THAT GESTURE, and it is separate from the
 * component for the reason `lib/groundEdit.js` is separate from
 * `GroundEditPanel.jsx`: this repo mounts no components in unit tests, so a
 * rule written inside JSX is a rule nothing can assert.
 *
 * --- IT IS NOT A DIFF, AND THAT IS THE WHOLE POINT ---
 *
 * `groundEditDiff` sends only what CHANGED, because stamping an audit trail
 * for fields nobody touched makes `updated_by` worth nothing. The rating is
 * the one field where that rule inverts: a rating goes stale by standing
 * still, so "good, again, today" is a fresh observation of the same state and
 * the server re-dates it deliberately (`lib/conditionRating.ts`). Re-using
 * the diff here would silently drop the single most useful update this
 * gesture can make — the seasonal re-confirmation.
 *
 * The one thing it will not send is a clear on something already unrated:
 * that is the empty patch the API answers 400 to, and it would claim a
 * correction that never happened.
 *
 * --- WHO MAY DO IT: THE SAME PEOPLE, DELIBERATELY ---
 *
 * `canUpdateCondition` is `canOfferGroundEdit`, and the delegation is the
 * decision rather than laziness. FAC-09 asks for a cheaper GESTURE, not a
 * wider PERMISSION:
 *
 *   - #165 already answered the neighbouring question the other way. FAC-03's
 *     "coaches and players can flag a net as needing attention" is NOT a
 *     write — it is #131's reporting path, a report queued for whoever owns
 *     the record. Letting a passer-by write the rating directly would build
 *     exactly what that ticket says not to build.
 *   - #211 (user-submitted ratings) is HELD and is a different concept: many
 *     visitors' judgements, many rows. `condition` is ONE value, the
 *     record-keeper's. Loosening this would ship a one-row version of #211
 *     in which the last visitor quietly wins.
 *   - There is no audit log (FAC-10 does not exist) and `updated_by` holds
 *     only the last writer, so a wrong rating is recoverable only because the
 *     people who can write one are few and identifiable.
 *
 * The gap that leaves is real and known: on a published or verified ground,
 * only an admin can re-rate. That is the club layer's hole (FAC-08, blocked
 * on #158), left open rather than papered over with a rule that would be hard
 * to tighten once people relied on it.
 *
 * --- ONE GESTURE FOR BOTH TABLES ---
 *
 * A venue and a net are rated from the same five words
 * (`api/src/lib/conditionRating.ts`, mirrored on the client by
 * `FACILITY_CONDITIONS`), dated by the same rule, and gated by the same
 * predicate — a facility has no gate of its own and is authorised through its
 * PARENT (`api/src/routes/groundGate.ts`). The only thing that differs is
 * where the patch is addressed, so that is the only thing this module
 * branches on.
 */

import { canOfferGroundEdit } from "./groundEdit";
import { FACILITY_CONDITIONS } from "./facilities";

/**
 * Unrated is the ABSENCE of a rating, never a sixth word — the API has no
 * `unknown` member and the columns are nullable precisely so that "nobody has
 * judged this" and "somebody judged it badly" stay distinguishable. Named so
 * that the clear action reads as a value in the UI rather than as a bare
 * `null` nobody can search for.
 */
export const CONDITION_UNRATED = null;

/** A word from the one vocabulary, or the unrated state. Anything else is a
 * hand-edited value the API would answer 400 to, and this says so first. */
export function isConditionChoice(value) {
  return value === CONDITION_UNRATED || FACILITY_CONDITIONS.includes(value);
}

/**
 * Where the patch goes.
 *
 * A facility is addressed as a CHILD of the ground, `(ground_id,
 * facility_id)`, never by bare id — the API has no route that would accept
 * one, on purpose (a facility id must not be able to answer "which ground is
 * this attached to"). Returns `null` when the target is not addressable, so a
 * caller cannot accidentally PATCH `/grounds/undefined`.
 */
export function conditionUpdatePath({ groundId, facilityId = null } = {}) {
  if (!groundId) return null;
  return facilityId ? `/grounds/${groundId}/facilities/${facilityId}` : `/grounds/${groundId}`;
}

/**
 * The patch body: EXACTLY ONE KEY, which is FAC-09's sentence expressed as
 * code. `condition_rated_at` is never sent — the observation date is the one
 * field on the row that could be backdated, and the server owns it (#164).
 */
export function conditionUpdateBody(next) {
  if (!isConditionChoice(next)) return null;
  return { condition: next };
}

/**
 * Is this tap worth a request?
 *
 * TRUE FOR THE SAME VALUE AGAIN — see the header; that is the re-confirmation
 * the date column exists for. FALSE only for clearing what is already
 * unrated, which is the empty patch the API refuses.
 */
export function conditionUpdateChanges(current, next) {
  if (!isConditionChoice(next)) return false;
  return !(next === CONDITION_UNRATED && (current ?? null) === CONDITION_UNRATED);
}

/**
 * Who may re-rate: whoever may correct the record, and nobody else.
 *
 * TAKES THE GROUND EVEN FOR A FACILITY, which is the client saying out loud
 * what `checkGroundCorrectionGate` enforces — a net is authorised through the
 * venue it hangs off. Passing the facility here would be a category error the
 * server would catch, but silently: it would simply refuse every time.
 *
 * Like `canOfferGroundEdit` this is the SUBSET the client can evaluate
 * honestly. The server also requires that no other player is relying on the
 * ground, which no client may be told; a refusal comes back as a 403 whose
 * prose is written to be shown to the person who pressed the button.
 */
export function canUpdateCondition(ground, user) {
  return canOfferGroundEdit(ground, user);
}
